import React from 'react';
import { SafeImg } from '../../SafeImg';
import { Btn, H1, Eyebrow, tidFor, E } from './_shared';
import ProductReviews from '@/components/storefront/ProductReviews';

// Extract the current site subdomain from URL: /site/:sub/... → sub
function useSiteSubdomain() {
  if (typeof window === 'undefined') return null;
  const m = window.location.pathname.match(/\/site\/([^/?#]+)/);
  return m ? m[1] : null;
}

/* ════════════════════════════════════════════════════════════════════════
 * NEW BLOCK TYPE — product_detail (PDP)
 *
 * Iter 175c — Section Audit. product.name · price · description · category
 * inline-editable; sizes & swatches editable as arrays via dotted-path.
 * Add-to-Cart label inline-editable.
 * ════════════════════════════════════════════════════════════════════════ */
export function ProductDetailBlock({ block, theme, E: EProps = {} }) {
  const { onEdit } = EProps;
  const c = block.content || {};
  const layout = block.layout || 'left-thumbs-right-info';
  const product = c.product || { name:'Sample Product', price:'$199', description:'Product description goes here. Best in class quality.' };
  const images = product.images || (product.image ? [product.image] : []);
  const ctaLabel = c.cta_label || (layout === 'two-col-fashion' ? 'Add to Cart' : 'Buy Now');
  const subdomain = useSiteSubdomain();
  const productId = product.id || product.product_id || null;
  const showReviews = !!(subdomain && productId && !onEdit);  // only on live public site

  if (layout === 'two-col-fashion') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-12" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10">
          <div className="aspect-[3/4]">{images[0] ? <SafeImg src={images[0]} alt={product.name} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-400" />}</div>
          <div className="flex flex-col justify-center">
            <E block={block} onEdit={onEdit} field="product.category" value={product.category || 'Fashion'} tag="p" className="text-xs uppercase tracking-wider opacity-60 font-bold" placeholder="Category" />
            <E block={block} onEdit={onEdit} field="product.name" value={product.name} tag="h1" className="text-3xl md:text-4xl font-bold mt-2" style={{ fontFamily: theme.headingFont }} placeholder="Product name" />
            <E block={block} onEdit={onEdit} field="product.price" value={product.price} tag="p" className="mt-3 text-2xl font-bold" style={{ color: theme.accent }} placeholder="$0" />
            <E block={block} onEdit={onEdit} field="product.description" value={product.description} tag="p" className="mt-6 opacity-75 text-sm leading-relaxed" placeholder="Product description" />
            <div className="mt-6">
              <p className="text-xs uppercase tracking-wider opacity-60 font-bold mb-2">Sizes</p>
              <div className="flex gap-2">{(product.sizes || ['XS','S','M','L','XL']).map((sz,i)=>(
                <span key={i} className="px-3 py-1.5 border border-gray-300 text-xs font-bold rounded cursor-text">
                  <E block={block} onEdit={onEdit} field={`product.sizes.${i}`} value={sz} tag="span" placeholder="Size" />
                </span>
              ))}</div>
            </div>
            <div className="mt-5">
              <p className="text-xs uppercase tracking-wider opacity-60 font-bold mb-2">Colors</p>
              <div className="flex gap-2">{(product.swatches || ['#EF4444','#3B82F6','#10B981','#000']).map((sw,i)=><button key={i} className="w-7 h-7 rounded-full border-2 border-white shadow" style={{ background: sw }} />)}</div>
            </div>
            <span className="mt-8 px-8 py-3 rounded-full text-sm font-bold uppercase tracking-wider text-white w-fit cursor-text" style={{ background: theme.accent || '#7C3AED' }}>
              <E block={block} onEdit={onEdit} field="cta_label" value={ctaLabel} tag="span" placeholder="Add to Cart" />
            </span>
          </div>
        </div>
        {showReviews && (
          <div className="max-w-6xl mx-auto mt-6">
            <ProductReviews subdomain={subdomain} productId={productId} />
          </div>
        )}
      </section>
    );
  }

  // left-thumbs-right-info (default)
  return (
    <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-12" style={{ background: theme.bg, color: theme.ink }}>
      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-8">
        <div className="md:col-span-2 flex md:flex-col gap-2">
          {(images.length ? images : [null,null,null,null]).slice(0,4).map((img,i)=>(
            <div key={i} className="aspect-square w-full bg-gray-100">{img && <SafeImg src={img} alt="" className="w-full h-full object-cover" />}</div>
          ))}
        </div>
        <div className="md:col-span-6 aspect-square bg-gray-100">{images[0] && <SafeImg src={images[0]} alt={product.name} className="w-full h-full object-cover" />}</div>
        <div className="md:col-span-4">
          <E block={block} onEdit={onEdit} field="product.category" value={product.category || 'Category'} tag="p" className="text-xs uppercase tracking-wider opacity-60 font-bold" placeholder="Category" />
          <E block={block} onEdit={onEdit} field="product.name" value={product.name} tag="h1" className="text-2xl md:text-3xl font-bold mt-2" style={{ fontFamily: theme.headingFont }} placeholder="Product name" />
          <E block={block} onEdit={onEdit} field="product.price" value={product.price} tag="p" className="mt-3 text-2xl font-bold" style={{ color: theme.accent }} placeholder="$0" />
          <E block={block} onEdit={onEdit} field="product.description" value={product.description} tag="p" className="mt-6 opacity-75 text-sm leading-relaxed" placeholder="Product description" />
          <span className="mt-8 w-full block py-3 rounded-full text-sm font-bold uppercase tracking-wider text-white text-center cursor-text" style={{ background: theme.accent || '#7C3AED' }}>
            <E block={block} onEdit={onEdit} field="cta_label" value={ctaLabel} tag="span" placeholder="Buy Now" />
          </span>
        </div>
      </div>
      {showReviews && (
        <div className="max-w-6xl mx-auto mt-6">
          <ProductReviews subdomain={subdomain} productId={productId} />
        </div>
      )}
    </section>
  );
}
